import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableRow,
  TableHead,
  TableCell,
  TableBody,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";

import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

import axios from "axios";
import html2pdf from "html2pdf.js";

import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import HeaderTitle from "../../../components/common/HeaderTitle/HeaderTitle";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import SearchBar from "../../../components/common/SearchBar/SearchBar";

import { API_ENDPOINT } from "../../../services/api/resources";

import {
  nextPageClick,
  previouPageClick,
} from "../../../utils/pagination";

import {
  normalizeSavedPriceCatalogRecord,
  toNumberValue,
} from "./priceListCatalogMapping";
import { enterprisePrimaryButtonSx } from "../../../components/common/upload/enterpriseUploadStyles";
import PriceListLineItemsModal from "./PriceListLineItemsModal";

const PRICE_LIST_PAGE_SIZE = 10;

/* ============================================================
   DATE FORMAT
============================================================ */

const formatSavedDate = (value) => {
  if (!value) return "-";

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ============================================================
   RECORD NORMALIZATION
============================================================ */

const normalizeSavedPriceCatalogRecordForTable = (record) => {
  const normalized =
    normalizeSavedPriceCatalogRecord(record);

  return {
    ...normalized,

    created_date:
      formatSavedDate(
        normalized.created_date,
      ),
  };
};

/* ============================================================
   SEARCH
============================================================ */

const recordMatchesSearch = (record, search) => {
  if (!search) return true;

  const normalizedSearch =
    search.toLowerCase();

  return [
    record?.document_number,
    record?.price_catalog_file_name,
    record?.filter_document_file_name,
    record?.catalog_source_type,
    record?.document_mode,
    record?.quotation_number,
    record?.vendor_name,
    record?.supplier,
    record?.company_details?.company_name,
  ].some((value) =>
    String(value || "")
      .toLowerCase()
      .includes(normalizedSearch),
  );
};

/* ============================================================
   LINE ITEMS
============================================================ */

const getRecordItems = (record) => {
  if (Array.isArray(record?.line_items)) {
    return record.line_items;
  }

  const filterResults =
    record?.matched_results?.filter_results || [];

  if (Array.isArray(filterResults)) {
    return filterResults.flatMap(
      (filter) =>
        filter?.matched_items || [],
    );
  }

  return [];
};

/* ============================================================
   SUPPLIER / COMPANY
============================================================ */

const getRecordSupplier = (record) =>
  record?.company_details?.company_name ||
  record?.vendor_name ||
  record?.supplier_name ||
  getRecordItems(record)?.[0]?.supplier ||
  "-";

/* ============================================================
   QUOTATION NUMBER
============================================================ */

const getQuotationNumber = (record) =>
  record?.quotation_number ||
  record?.quotation_code ||
  record?.token ||
  record?.document_number ||
  "Vendor-Quotation";

/* ============================================================
   CURRENCY
============================================================ */

const getRecordCurrency = (record) =>
  record?.currency ||
  getRecordItems(record)?.find(
    (item) => item?.currency,
  )?.currency ||
  "INR";

/* ============================================================
   TOTAL
============================================================ */

const getRecordTotal = (record) => {
  /*
   * Prefer backend grand total.
   */
  const apiGrandTotal =
    record?.grand_total ?? record?.matched_results?.grand_total;

  if (
    apiGrandTotal !== undefined &&
    apiGrandTotal !== null &&
    apiGrandTotal !== ""
  ) {
    return toNumberValue(apiGrandTotal);
  }

  return getRecordItems(record).reduce(
    (sum, item) => {
      /*
       * New API
       */
      const totalPrice =
        toNumberValue(
          item?.total_price,
          null,
        );

      if (totalPrice !== null) {
        return sum + totalPrice;
      }

      /*
       * Old API
       */
      const lineTotal =
        toNumberValue(
          item?.total_line_value,
          null,
        );

      if (lineTotal !== null) {
        return sum + lineTotal;
      }

      return (
        sum +
        toNumberValue(
          item?.quantity,
        ) *
          toNumberValue(
            item?.unit_price,
          )
      );
    },
    0,
  );
};

/* ============================================================
   MONEY FORMAT
============================================================ */

const formatMoney = (value) => {
  const cleanedValue =
    typeof value === "string"
      ? value
          .replace(/,/g, "")
          .trim()
      : value;

  const numberValue =
    Number(cleanedValue);

  if (!Number.isFinite(numberValue)) {
    return "0.00";
  }

  return numberValue.toLocaleString(
    "en-IN",
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    },
  );
};

const getCurrencySymbol = (currency) => {
  const normalizedCurrency = String(currency || "INR").toUpperCase();
  return {
    INR: "₹",
    USD: "$",
    EUR: "€",
    GBP: "£",
  }[normalizedCurrency] || normalizedCurrency;
};

/* ============================================================
   HTML ESCAPE
============================================================ */

const escapeHtml = (value) =>
  String(value ?? "-")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/* ============================================================
   TAX FORMAT
============================================================ */

const formatTaxSummary = (tax = {}) =>
  [
    Array.isArray(tax?.gst) &&
    tax.gst.length
      ? `GST ${tax.gst.join(", ")}`
      : "",

    Array.isArray(tax?.cgst) &&
    tax.cgst.length
      ? `CGST ${tax.cgst.join(", ")}`
      : "",

    Array.isArray(tax?.sgst) &&
    tax.sgst.length
      ? `SGST ${tax.sgst.join(", ")}`
      : "",

    Array.isArray(tax?.igst) &&
    tax.igst.length
      ? `IGST ${tax.igst.join(", ")}`
      : "",

    tax?.tax_amount
      ? `Tax Amount ${tax.tax_amount}`
      : "",
  ]
    .filter(Boolean)
    .join(" | ") || "-";

/* ============================================================
   MAIN COMPONENT
============================================================ */

const PriceListHome = ({
  onUploadPriceCatalog,
  onUploadPriceList,
  catalogRecords,
  priceListRecords,
  activeTab,
  setActiveTab,
}) => {
  const loginData =
    useSelector(
      (state) => state.loginData.value,
    );

  const navigate =
    useNavigate();

  const [
    localActiveTab,
    setLocalActiveTab,
  ] = useState(activeTab || 0);

  const [
    catalogSearch,
    setCatalogSearch,
  ] = useState("");

  const [catalogPageNumber, setCatalogPageNumber] = useState(1);
  const [savedCatalogRecords, setSavedCatalogRecords] = useState([]);
  const [catalogPagination, setCatalogPagination] = useState({
    page: 1,
    page_size: PRICE_LIST_PAGE_SIZE,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
  });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [catalogItemsOpen, setCatalogItemsOpen] = useState(false);
  const [selectedCatalogRecord, setSelectedCatalogRecord] = useState(null);

  const [
    priceListSearch,
    setPriceListSearch,
  ] = useState("");

  const [
    debouncedPriceListSearch,
    setDebouncedPriceListSearch,
  ] = useState("");

  const [
    priceListPageNumber,
    setPriceListPageNumber,
  ] = useState(1);

  const [
    savedPriceListRecords,
    setSavedPriceListRecords,
  ] = useState([]);

  const [
    priceListPagination,
    setPriceListPagination,
  ] = useState({
    page: 1,
    page_size:
      PRICE_LIST_PAGE_SIZE,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_previous: false,
    count: 0,
  });

  const [
    priceListLoading,
    setPriceListLoading,
  ] = useState(false);

  const [
    priceListError,
    setPriceListError,
  ] = useState("");

  const selectedTab =
    activeTab ??
    localActiveTab;

  /* ============================================================
     TAB CHANGE
  ============================================================ */

  const handleTabChange = (
    _,
    value,
  ) => {
    if (setActiveTab) {
      setActiveTab(value);
    }

    setLocalActiveTab(value);
  };

  /* ============================================================
     TOKENS
  ============================================================ */

  const organizationToken =
    loginData?.organization_token ||
    loginData?.org_token ||
    loginData?.user
      ?.organization_token ||
    loginData?.user?.org_token ||
    "";

  const authToken =
    loginData?.auth_token ||
    loginData?.access_token ||
    loginData?.token ||
    loginData?.user
      ?.auth_token ||
    "";

  /* ============================================================
     PRICE CATALOG SEARCH
  ============================================================ */

  const filteredCatalogRecords = useMemo(
    () => (savedCatalogRecords.length > 0 ? savedCatalogRecords : catalogRecords)
      .map(normalizeSavedPriceCatalogRecordForTable),
    [catalogRecords, savedCatalogRecords],
  );

  /* ============================================================
     SEARCH DEBOUNCE
  ============================================================ */

  useEffect(() => {
    if (selectedTab !== 0) return;

    const fetchSavedCatalogRecords = async () => {
      try {
        setCatalogLoading(true);
        setCatalogError("");
        const headers = { "Content-Type": "application/json" };
        if (authToken) headers.Authorization = `Token ${authToken}`;

        const catalogPayload = {
          page: catalogPageNumber,
          page_size: PRICE_LIST_PAGE_SIZE,
          ...(organizationToken ? { organization_token: organizationToken } : {}),
          ...(catalogSearch.trim() ? { search: catalogSearch.trim() } : {}),
        };

        const response = await axios.post(
          API_ENDPOINT.view_price_catalog_document,
          catalogPayload,
          { headers },
        );
        const payload = response?.data || {};
        if (payload?.status === false || payload?.success === false) {
          setCatalogError(payload?.message || "Unable to fetch price catalogs.");
          return;
        }
        const records = Array.isArray(payload?.data) ? payload.data : [];
        const totalCount = Number(payload?.total_count ?? records.length);
        setSavedCatalogRecords(records.map(normalizeSavedPriceCatalogRecordForTable));
        setCatalogPagination({
          page: Number(payload?.page || catalogPageNumber),
          page_size: Number(payload?.page_size || PRICE_LIST_PAGE_SIZE),
          total_count: totalCount,
          total_pages: Math.max(1, Math.ceil(totalCount / PRICE_LIST_PAGE_SIZE)),
          has_next: Number(payload?.page || catalogPageNumber) < Math.ceil(totalCount / PRICE_LIST_PAGE_SIZE),
          has_previous: Number(payload?.page || catalogPageNumber) > 1,
        });
      } catch (error) {
        setCatalogError(error?.response?.data?.message || "Unable to fetch price catalogs.");
      } finally {
        setCatalogLoading(false);
      }
    };

    fetchSavedCatalogRecords();
  }, [selectedTab, authToken, organizationToken, catalogSearch, catalogPageNumber]);

  useEffect(() => {
    const timeoutId =
      setTimeout(() => {
        setDebouncedPriceListSearch(
          priceListSearch.trim(),
        );
      }, 400);

    return () =>
      clearTimeout(timeoutId);
  }, [priceListSearch]);

  /* ============================================================
     FETCH SAVED QUOTATIONS
  ============================================================ */

  useEffect(() => {
    if (selectedTab !== 1) {
      return;
    }

    const fetchSavedPriceListRecords =
      async () => {
        if (!organizationToken) {
          setPriceListError(
            "Organization token not found. Please login again.",
          );

          setSavedPriceListRecords(
            [],
          );

          setPriceListPagination(
            (prev) => ({
              ...prev,
              total_count: 0,
              total_pages: 0,
            }),
          );

          return;
        }

        try {
          setPriceListLoading(
            true,
          );

          setPriceListError("");

          const headers = {
            "Content-Type":
              "application/json",
          };

          if (authToken) {
            headers.Authorization =
              `Token ${authToken}`;
          }

          const response =
            await axios.post(
              API_ENDPOINT
                .get_price_catalog_filter,
              {
                organization_token:
                  organizationToken,

                page:
                  Number(
                    priceListPageNumber,
                  ) || 1,

                page_size:
                  PRICE_LIST_PAGE_SIZE,

                search:
                  debouncedPriceListSearch,
              },
              {
                headers,
              },
            );

          const payload =
            response?.data || {};

          if (
            payload?.status === false ||
            payload?.success === false
          ) {
            setPriceListError(
              payload?.message ||
                "Unable to fetch saved price lists.",
            );

            setSavedPriceListRecords(
              [],
            );

            return;
          }

          const responseData =
            payload?.data || {};

          const responseRecords =
            Array.isArray(
              responseData,
            )
              ? responseData
              : Array.isArray(
                    responseData?.records,
                  )
                ? responseData.records
                : [];

          setSavedPriceListRecords(
            responseRecords.map(
              normalizeSavedPriceCatalogRecordForTable,
            ),
          );

          setPriceListPagination({
            page:
              responseData?.pagination
                ?.page ||
              Number(
                priceListPageNumber,
              ) ||
              1,

            page_size:
              responseData?.pagination
                ?.page_size ||
              PRICE_LIST_PAGE_SIZE,

            total_count:
              responseData?.pagination
                ?.total_count ||
              responseRecords.length,

            total_pages:
              responseData?.pagination
                ?.total_pages ||
              1,

            has_next: Boolean(
              responseData?.pagination
                ?.has_next,
            ),

            has_previous:
              Boolean(
                responseData
                  ?.pagination
                  ?.has_previous,
              ),

            count:
              responseData?.pagination
                ?.count ||
              responseRecords.length,
          });
        } catch (error) {
          console.error(
            "Error fetching saved price catalog filters:",
            error,
          );

          setPriceListError(
            error?.response?.data
              ?.message ||
              error?.response?.data
                ?.response ||
              "Unable to fetch saved price lists. Please try again.",
          );

          setSavedPriceListRecords(
            [],
          );
        } finally {
          setPriceListLoading(
            false,
          );
        }
      };

    fetchSavedPriceListRecords();
  }, [
    selectedTab,
    authToken,
    debouncedPriceListSearch,
    organizationToken,
    priceListPageNumber,
  ]);

  /* ============================================================
     LOCAL QUOTATIONS
  ============================================================ */

  const filteredLocalPriceListRecords =
    useMemo(
      () =>
        priceListRecords
          .map(
            (
              record,
            ) =>
              normalizeSavedPriceCatalogRecordForTable(
                record,
              ),
          )
          .filter(
            (record) =>
              recordMatchesSearch(
                record,
                debouncedPriceListSearch,
              ),
          ),

      [
        debouncedPriceListSearch,
        priceListRecords,
      ],
    );

  /* ============================================================
     MERGE LOCAL + SAVED
  ============================================================ */

  const displayedPriceListRecords =
    useMemo(() => {
      const savedRecordKeys =
        new Set(
          savedPriceListRecords.map(
            (record) =>
              String(
                record?.token ||
                  record?.id ||
                  "",
              ),
          ),
        );

      const localOnlyRecords =
        filteredLocalPriceListRecords.filter(
          (record) =>
            !savedRecordKeys.has(
              String(
                record?.token ||
                  record?.id ||
                  "",
              ),
            ),
        );

      return [
        ...localOnlyRecords,
        ...savedPriceListRecords,
      ];
    }, [
      filteredLocalPriceListRecords,
      savedPriceListRecords,
    ]);

  const priceListTotalCount =
    Math.max(
      priceListPagination.total_count,
      displayedPriceListRecords.length,
    );

  const priceListTotalPages =
    priceListPagination.total_pages ||
    (displayedPriceListRecords.length >
    0
      ? 1
      : 0);

  /* ============================================================
     VIEW QUOTATION
  ============================================================ */

  const handleAction = (record) => {
    navigate(
      "/Buyer_dashboard/price_list/create_quotation",
      {
        state: {
          selectedPriceList:
            record,
        },
      },
    );
  };

  /* ============================================================
     DOWNLOAD PROFESSIONAL VENDOR QUOTATION
  ============================================================ */

  const handleDownloadQuotation =
    async (record) => {
      const items =
        getRecordItems(record);

      const companyDetails =
        record?.company_details ||
        {};

      /* ===============================
         COMPANY INFORMATION
      =============================== */

      const companyName =
        companyDetails?.company_name ||
        record?.vendor_name ||
        record?.supplier_name ||
        getRecordSupplier(record) ||
        "-";

      const companyAddress =
        companyDetails?.location ||
        record?.supplier_address ||
        record?.vendor_address ||
        record?.address ||
        "-";

      const gstNumber =
        companyDetails?.gst_number ||
        record?.supplier_gst ||
        record?.vendor_gst ||
        record?.gst_number ||
        "-";

      const panNumber =
        companyDetails?.pan_number ||
        record?.supplier_pan ||
        record?.vendor_pan ||
        "-";

      const warranty =
        companyDetails?.warranty ||
        record?.warranty ||
        "-";

      const taxDetails =
        companyDetails?.tax ||
        record?.supplier_tax ||
        {};

      const taxSummary =
        formatTaxSummary(
          taxDetails,
        );

      const currency =
        getRecordCurrency(record) ||
        "INR";

      /* ===============================
         SUBTOTAL
      =============================== */

      const apiSubtotal =
        record?.matched_results
          ?.total_price_without_tax;

      const calculatedSubtotal =
        items.reduce(
          (sum, item) => {
            const priceWithoutTax =
              toNumberValue(
                item?.price_without_tax,
                null,
              );

            if (
              priceWithoutTax !==
              null
            ) {
              return (
                sum +
                priceWithoutTax
              );
            }

            return (
              sum +
              toNumberValue(
                item?.quantity,
              ) *
                toNumberValue(
                  item?.unit_price,
                )
            );
          },
          0,
        );

      const subtotal =
        apiSubtotal !== undefined &&
        apiSubtotal !== null &&
        apiSubtotal !== ""
          ? toNumberValue(
              apiSubtotal,
            )
          : calculatedSubtotal;

      /* ===============================
         TAX AMOUNT
      =============================== */

      const taxTotal =
        items.reduce(
          (sum, item) => {
            const lineTotal =
              toNumberValue(
                item?.total_price ||
                  item?.total_line_value,
                null,
              );

            const priceWithoutTax =
              toNumberValue(
                item?.price_without_tax,
                null,
              );

            if (
              lineTotal !== null &&
              priceWithoutTax !==
                null
            ) {
              return (
                sum +
                Math.max(
                  lineTotal -
                    priceWithoutTax,
                  0,
                )
              );
            }

            const base =
              toNumberValue(
                item?.quantity,
              ) *
              toNumberValue(
                item?.unit_price,
              );

            const taxPercent =
              toNumberValue(
                item?.tax ||
                  item?.tax_percent,
              );

            return (
              sum +
              (base *
                taxPercent) /
                100
            );
          },
          0,
        );

      /* ===============================
         GRAND TOTAL
      =============================== */

      const apiGrandTotal =
        record?.matched_results
          ?.grand_total;

      const grandTotal =
        apiGrandTotal !==
          undefined &&
        apiGrandTotal !== null &&
        apiGrandTotal !== ""
          ? toNumberValue(
              apiGrandTotal,
            )
          : subtotal + taxTotal;

      /* ===============================
         DATE
      =============================== */

      const quotationDate =
        record?.created_date &&
        record?.created_date !==
          "-"
          ? record.created_date
          : new Date().toLocaleDateString(
              "en-IN",
              {
                day: "2-digit",
                month: "short",
                year: "numeric",
              },
            );

      const quotationNumber =
        getQuotationNumber(
          record,
        );

      /* ===============================
         HTML WRAPPER
      =============================== */

      const wrapper =
        document.createElement(
          "div",
        );

      wrapper.style.padding =
        "28px";

      wrapper.style.fontFamily =
        "Arial, Helvetica, sans-serif";

      wrapper.style.color =
        "#0f172a";

      wrapper.style.backgroundColor =
        "#ffffff";

      wrapper.innerHTML = `
        <div
          style="
            width:100%;
            box-sizing:border-box;
            background:#ffffff;
          "
        >

          <!-- =================================================
               QUOTATION HEADER
          ================================================== -->

          <div
            style="
              display:flex;
              justify-content:space-between;
              align-items:flex-start;
              gap:32px;
              border-bottom:3px solid #075b97;
              padding-bottom:22px;
              margin-bottom:24px;
            "
          >

            <!-- LEFT HEADER -->

            <div
              style="
                width:40%;
              "
            >

              <h1
                style="
                  margin:0;
                  padding:0;
                  color:#075b97;
                  font-size:36px;
                  line-height:1.1;
                  font-weight:800;
                  letter-spacing:0.8px;
                "
              >
                QUOTATION
              </h1>

              <div
                style="
                  margin-top:17px;
                  font-size:16px;
                  line-height:1.8;
                  color:#334155;
                "
              >

              

                <div>
                  <strong>
                    Date:
                  </strong>

                  ${escapeHtml(
                    quotationDate,
                  )}
                </div>

              </div>

            </div>


          

            <div
              style="
                width:60%;
                text-align:right;
              "
            >

              <div
                style="
                  color:#64748b;
                  font-size:15px;
                  font-weight:700;
                  text-transform:uppercase;
                  letter-spacing:1px;
                  margin-bottom:8px;
                "
              >
                Vendor Details
              </div>

              <!-- COMPANY NAME -->

              <div
                style="
                  font-size:24px;
                  line-height:1.3;
                  font-weight:800;
                  color:#0f172a;
                  margin-bottom:12px;
                "
              >
                ${escapeHtml(
                  companyName,
                )}
              </div>

              <div
                style="
                  font-size:16px;
                  line-height:1.8;
                  color:#334155;
                "
              >

                <div>
                  <strong>
                    Location:
                  </strong>

                  ${escapeHtml(
                    companyAddress,
                  )}
                </div>

                <div>
                  <strong>
                    GSTIN:
                  </strong>

                  ${escapeHtml(
                    gstNumber,
                  )}
                </div>

                <div>
                  <strong>
                    PAN:
                  </strong>

                  ${escapeHtml(
                    panNumber,
                  )}
                </div>

              </div>

            </div>

          </div>


          <!-- =================================================
               ITEMS TITLE
          ================================================== -->

          <div
            style="
              font-size:19px;
              font-weight:700;
              color:#0f172a;
              margin-bottom:12px;
            "
          >
            Quotation Items
          </div>


          <!-- =================================================
               ITEMS TABLE
          ================================================== -->

          <table
            style="
              width:100%;
              border-collapse:collapse;
              table-layout:auto;
              font-size:13px;
            "
          >

            <thead>

              <tr
                style="
                  background:#e0e9ee;
                  color:#0f172a;
                "
              >

                ${[
                  "Sl No",
                  "Part No",
                  "Item Name",
                  "Qty",
                  "UOM",
                  "Unit Price",
                  "Tax",
                  "Total",
                ]
                  .map(
                    (head) => `
                      <th
                        style="
                          border:1px solid #b6c2cb;
                          padding:11px 8px;
                          text-align:left;
                          font-size:13px;
                          font-weight:700;
                          white-space:nowrap;
                        "
                      >
                        ${head}
                      </th>
                    `,
                  )
                  .join("")}

              </tr>

            </thead>


            <tbody>

              ${items
                .map(
                  (
                    item,
                    index,
                  ) => {
                    const itemCode =
                      item?.item_code ||
                      item?.part_no ||
                      item?.item_id ||
                      item?.sku_number ||
                      "-";

                    const itemName =
                      item?.item_name ||
                      "-";

                    const quantity =
                      item?.quantity ||
                      "-";

                    const uom =
                      item?.uom ||
                      item?.unit ||
                      "-";

                    /*
                     * DIRECT UNIT PRICE
                     */
                    const unitPrice =
                      toNumberValue(
                        item?.unit_price ||
                          item?.net_unit_price,
                      );

                    /*
                     * DIRECT TAX %
                     */
                    const itemTax =
                      item?.tax ||
                      item?.tax_percent ||
                      "-";

                    /*
                     * DIRECT TOTAL PRICE
                     */
                    const itemTotal =
                      toNumberValue(
                        item?.total_price ||
                          item?.total_line_value,
                      );

                    return `
                      <tr>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                          "
                        >
                          ${index + 1}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            white-space:nowrap;
                          "
                        >
                          ${escapeHtml(
                            itemCode,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            line-height:1.45;
                          "
                        >
                          ${escapeHtml(
                            itemName,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            text-align:center;
                          "
                        >
                          ${escapeHtml(
                            quantity,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            text-align:center;
                          "
                        >
                          ${escapeHtml(
                            uom,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            text-align:right;
                            white-space:nowrap;
                          "
                        >
                          ${escapeHtml(
                            currency,
                          )}
                          ${formatMoney(
                            unitPrice,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            text-align:center;
                            white-space:nowrap;
                          "
                        >
                          ${escapeHtml(
                            itemTax,
                          )}
                        </td>

                        <td
                          style="
                            border:1px solid #d9e0e6;
                            padding:10px 8px;
                            vertical-align:top;
                            text-align:right;
                            font-weight:700;
                            white-space:nowrap;
                          "
                        >
                          ${escapeHtml(
                            currency,
                          )}
                          ${formatMoney(
                            itemTotal,
                          )}
                        </td>

                      </tr>
                    `;
                  },
                )
                .join("")}

            </tbody>

          </table>


          <!-- =================================================
               WARRANTY + TOTAL SECTION
          ================================================== -->

          <div
            style="
              display:flex;
              justify-content:space-between;
              align-items:flex-start;
              gap:24px;
              margin-top:26px;
              page-break-inside:avoid;
            "
          >

            <!-- WARRANTY -->

            <div
              style="
                width:54%;
                box-sizing:border-box;
                border:1px solid #cbd5e1;
                border-radius:5px;
                background:#f8fafc;
                padding:17px;
              "
            >

              <div
                style="
                  color:#0f172a;
                  font-size:17px;
                  font-weight:700;
                  margin-bottom:9px;
                "
              >
                Warranty
              </div>

              <div
                style="
                  font-size:14px;
                  color:#475569;
                  line-height:1.65;
                "
              >
                ${escapeHtml(
                  warranty,
                )}
              </div>

            </div>


            <!-- TOTAL BOX -->

            <div
              style="
                width:330px;
                box-sizing:border-box;
                border:1px solid #cbd5e1;
                border-radius:5px;
                background:#f8fafc;
                padding:17px;
              "
            >

              <!-- SUBTOTAL -->

              <div
                style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  gap:15px;
                  margin-bottom:13px;
                  font-size:14px;
                "
              >

                <span>
                  Subtotal
                  <br/>
                  <span
                    style="
                      color:#64748b;
                      font-size:11px;
                    "
                  >
                    Excl. Tax
                  </span>
                </span>

                <strong
                  style="
                    white-space:nowrap;
                  "
                >
                  ${escapeHtml(
                    currency,
                  )}
                  ${formatMoney(
                    subtotal,
                  )}
                </strong>

              </div>


              <!-- TAX -->

              <div
                style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  gap:15px;
                  margin-bottom:13px;
                  font-size:14px;
                "
              >

                <span>
                  Tax
                </span>

                <strong
                  style="
                    text-align:right;
                  "
                >
                  ${escapeHtml(
                    taxSummary,
                  )}
                </strong>

              </div>


              <!-- TAX VALUE -->

              <div
                style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  gap:15px;
                  margin-bottom:13px;
                  font-size:14px;
                "
              >

                <span>
                  Tax Amount
                </span>

                <strong
                  style="
                    white-space:nowrap;
                  "
                >
                  ${escapeHtml(
                    currency,
                  )}
                  ${formatMoney(
                    taxTotal,
                  )}
                </strong>

              </div>


              <!-- DIVIDER -->

              <div
                style="
                  border-top:1px solid #cbd5e1;
                  margin:13px 0;
                "
              ></div>


              <!-- GRAND TOTAL -->

              <div
                style="
                  display:flex;
                  justify-content:space-between;
                  align-items:center;
                  gap:15px;
                  color:#075b97;
                "
              >

                <span
                  style="
                    font-size:17px;
                    font-weight:700;
                  "
                >
                  Grand Total
                </span>

                <strong
                  style="
                    font-size:19px;
                    white-space:nowrap;
                  "
                >
                  ${escapeHtml(
                    currency,
                  )}
                  ${formatMoney(
                    grandTotal,
                  )}
                </strong>

              </div>

            </div>

          </div>


          <!-- =================================================
               AUTHORIZED SIGNATURE
          ================================================== -->

          <div
            style="
              margin-top:38px;
              display:flex;
              justify-content:flex-end;
              page-break-inside:avoid;
            "
          >

            <div
              style="
                width:280px;
                text-align:right;
              "
            >

              <div
                style="
                  font-size:15px;
                  font-weight:700;
                  color:#0f172a;
                "
              >
                For ${escapeHtml(
                  companyName,
                )}
              </div>

              <div
                style="
                  height:48px;
                "
              ></div>

              <div
                style="
                  padding-top:7px;
                  border-top:1px solid #64748b;
                  font-size:13px;
                  color:#475569;
                "
              >
                Authorized Signatory
              </div>

            </div>

          </div>


          <!-- =================================================
               FOOTER
          ================================================== -->

          <div
            style="
              margin-top:28px;
              padding-top:12px;
              border-top:1px solid #e2e8f0;
              text-align:center;
              color:#64748b;
              font-size:11px;
              line-height:1.5;
            "
          >
            This quotation is system generated based on
            the selected vendor price catalog.
          </div>

        </div>
      `;

      /* ========================================================
         PDF GENERATE
      ======================================================== */

      await html2pdf()
        .set({
          margin: [
            8,
            8,
            8,
            8,
          ],

          filename:
            `${quotationNumber || "Vendor_Quotation"}`,

          image: {
            type: "jpeg",
            quality: 0.98,
          },

          html2canvas: {
            scale: 2.5,
            useCORS: true,
            letterRendering: true,
            backgroundColor:
              "#ffffff",
          },

          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "portrait",
          },

          pagebreak: {
            mode: [
              "css",
              "legacy",
            ],
          },
        })
        .from(wrapper)
        .save();
    };

  /* ============================================================
     UI
  ============================================================ */

  return (
    <Box sx={{ p: 2 }}>
      {/* ========================================================
          HEADER
      ======================================================== */}

      <Box
        sx={{
          mt: 1,
          mb: 2,

          display: "flex",

          justifyContent:
            "space-between",

          alignItems:
            "center",

          flexWrap:
            "wrap",
        }}
      >
        <HeaderTitle
          titleText="Price List"
        />

        <Box
          sx={{
            display:
              "flex",

            alignItems:
              "center",

            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="small"
            sx={enterprisePrimaryButtonSx}
            onClick={
              onUploadPriceCatalog
            }
          >
            Price Catalog
          </Button>

          <Button
            variant="contained"
            size="small"
            sx={enterprisePrimaryButtonSx}
            onClick={
              onUploadPriceList
            }
          >
            Generate Quotation
          </Button>
        </Box>
      </Box>

      {/* ========================================================
          TABS
      ======================================================== */}

      <Box
        sx={{
          borderBottom: 1,
          borderColor:
            "divider",
          mb: 1,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={
            handleTabChange
          }
        >
          <Tab
            label="Price Catalog"
            sx={{
              textTransform:
                "none",

              fontFamily:
                "GilroyBold",

              fontSize:
                "16px",
            }}
          />

          <Tab
            label="Quotation"
            sx={{
              textTransform:
                "none",

              fontFamily:
                "GilroyBold",

              fontSize:
                "16px",
            }}
          />
        </Tabs>
      </Box>

      {/* ========================================================
          PRICE CATALOG TAB
      ======================================================== */}

      {selectedTab === 0 && (
        <Box
          sx={{
            mt: "20px",
            width: "100%",
            overflowX: "auto",
          }}
        >
          <Box
            sx={{
              flex: 1,
              mb: 2,
            }}
          >
            <SearchBar
              value={
                catalogSearch
              }
              onChange={(
                value,
              ) => {
                setCatalogSearch(
                  value,
                );
                setCatalogPageNumber(1);
              }}
            />
          </Box>

          {catalogError ? (
            <Typography sx={{ color: "#b91c1c", fontSize: 14, mb: 1 }}>
              {catalogError}
            </Typography>
          ) : null}

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,

              boxShadow:
                "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Sl No",
                    "Price List No",
                    "Company Name",
                    "Items",
                    "Create Date",
                    "Grand Total",
                    "Action",
                  ].map(
                    (
                      header,
                      index,
                    ) => (
                      <TableCell
                        key={
                          index
                        }
                        sx={{
                          fontWeight:
                            600,

                          fontFamily:
                            "GilroySemiBold",

                          backgroundColor:
                            "#e0e9ee",

                          fontSize:
                            "14px",

                          borderBottom:
                            "1px solid #e2e8f0",
                        }}
                      >
                        {
                          header
                        }
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {catalogLoading && (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                      <CircularProgress size={22} />
                    </TableCell>
                  </TableRow>
                )}
                {filteredCatalogRecords.map(
                  (
                    item,
                    index,
                  ) => (
                    <TableRow
                      key={
                        index
                      }
                      sx={{
                        backgroundColor:
                          "white",

                        "&:hover":
                          {
                            backgroundColor:
                              "#f5f5f5",
                          },

                        borderBottom:
                          "1px solid #e2e8f0",
                      }}
                    >
                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                        {(catalogPageNumber - 1) * PRICE_LIST_PAGE_SIZE + index + 1}
                      </TableCell>

                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                        {item.price_list_no || item.document_id || item.file_name || item.document_number || item.token || ""}
                      </TableCell>

                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                        {item.company_name || item.vendor_details?.company_name || item.company_details?.company_name || item.vendor_name || item.supplier_name || ""}
                      </TableCell>

                      <TableCell sx={{ fontFamily: "GilroyMedium", fontSize: "14px" }}>
                        {item.row_count ?? getRecordItems(item).length}
                      </TableCell>

                      <TableCell
                        sx={{
                          fontFamily:
                            "GilroyMedium",

                          fontSize:
                            "14px",
                        }}
                      >
                        {
                          item.created_date
                        }
                      </TableCell>

                      <TableCell sx={{ fontFamily: "GilroySemiBold", fontSize: "14px", color: "#075b97", whiteSpace: "nowrap" }}>
                        {getCurrencySymbol(item.currency || getRecordCurrency(item))} {formatMoney(item.grand_total ?? getRecordTotal(item))}
                      </TableCell>

                      <TableCell>
                        <Tooltip title="View price list items" arrow>
                          <IconButton
                            onClick={() => {
                              setSelectedCatalogRecord(item);
                              setCatalogItemsOpen(true);
                            }}
                            sx={{
                              color: "#075b97",
                              border: "1px solid #cbd5e1",
                              borderRadius: 1.5,
                              width: 42,
                              height: 42,
                            }}
                          >
                            <VisibilityOutlinedIcon sx={{ fontSize: "19px" }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ),
                )}

                {!catalogLoading && filteredCatalogRecords.length === 0 && (
                  <TableRow>
                    <TableCell
                        colSpan={7}
                      sx={{
                        fontFamily:
                          "GilroyMedium",

                        fontSize:
                          "14px",

                        textAlign:
                          "center",
                      }}
                    >
                      No price catalog
                      records found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginantionComponent
            currentPage={catalogPagination.page}
            itemsPerPage={catalogPagination.total_count}
            totalItems={catalogPagination.total_pages}
            disablePrevious={!catalogPagination.has_previous}
            disableNext={!catalogPagination.has_next}
            previouPageClick={() => previouPageClick(catalogPageNumber, setCatalogPageNumber)}
            nextPageClick={() => nextPageClick(catalogPageNumber, setCatalogPageNumber, { page_count: catalogPagination.total_pages })}
            pageNumber={catalogPageNumber}
            setPageNumber={setCatalogPageNumber}
          />
        </Box>
      )}

      {/* ========================================================
          QUOTATION TAB
      ======================================================== */}

      {selectedTab === 1 && (
        <Box
          sx={{
            mt: "20px",
            width: "100%",
            overflowX: "auto",
          }}
        >
          {/* SEARCH */}

          <Box
            sx={{
              flex: 1,
              mb: 2,
            }}
          >
            <SearchBar
              value={
                priceListSearch
              }
              onChange={(
                value,
              ) => {
                setPriceListSearch(
                  value,
                );

                setPriceListPageNumber(
                  1,
                );
              }}
            />
          </Box>

          {/* ERROR */}

          {priceListError ? (
            <Typography
              sx={{
                color:
                  "#b91c1c",

                fontSize:
                  14,

                mb: 1,
              }}
            >
              {
                priceListError
              }
            </Typography>
          ) : null}

          {/* ====================================================
              QUOTATION TABLE
          ==================================================== */}

          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 2,

              boxShadow:
                "0 2px 10px rgba(0,0,0,0.08)",
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {[
                    "Sl No",
                    "Supplier",
                    "Items",
                    "Currency",
                    "Total Value",
                    "Created Date",
                    "Action",
                  ].map(
                    (
                      header,
                      index,
                    ) => (
                      <TableCell
                        key={
                          index
                        }
                        sx={{
                          width:
                            header ===
                            "Action"
                              ? "180px"
                              : "auto",

                          minWidth:
                            header ===
                            "Action"
                              ? "180px"
                              : "auto",

                          fontWeight:
                            600,

                          fontFamily:
                            "GilroySemiBold",

                          backgroundColor:
                            "#e0e9ee",

                          fontSize:
                            "14px",

                          borderBottom:
                            "1px solid #e2e8f0",
                        }}
                      >
                        {
                          header
                        }
                      </TableCell>
                    ),
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {/* LOADING */}

                {priceListLoading && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        7
                      }
                      sx={{
                        fontFamily:
                          "GilroyMedium",

                        fontSize:
                          "14px",

                        textAlign:
                          "center",

                        py: 4,
                      }}
                    >
                      <CircularProgress
                        size={
                          22
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}

                {/* DATA */}

                {!priceListLoading &&
                  displayedPriceListRecords.map(
                    (
                      item,
                      index,
                    ) => (
                      <TableRow
                        key={
                          item?.id ||
                          item?.token ||
                          index
                        }
                        sx={{
                          backgroundColor:
                            "white",

                          "&:hover":
                            {
                              backgroundColor:
                                "#f5f5f5",
                            },

                          borderBottom:
                            "1px solid #e2e8f0",
                        }}
                      >
                        {/* SL NO */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroyMedium",

                            fontSize:
                              "14px",
                          }}
                        >
                          {(priceListPageNumber -
                            1) *
                            PRICE_LIST_PAGE_SIZE +
                            index +
                            1}
                        </TableCell>

                        {/* SUPPLIER */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroySemiBold",

                            fontWeight:
                              600,

                            fontSize:
                              "14px",

                            color:
                              "#0f172a",
                          }}
                        >
                          {getRecordSupplier(
                            item,
                          )}
                        </TableCell>

                        {/* ITEMS */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroyMedium",

                            fontSize:
                              "14px",
                          }}
                        >
                          {getRecordItems(
                            item,
                          ).length}
                        </TableCell>

                        {/* CURRENCY */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroyMedium",

                            fontSize:
                              "14px",
                          }}
                        >
                          {getRecordCurrency(
                            item,
                          )}
                        </TableCell>

                        {/* TOTAL */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroySemiBold",

                            fontSize:
                              "14px",

                            color:
                              "#075b97",
                          }}
                        >
                          {formatMoney(
                            getRecordTotal(
                              item,
                            ),
                          )}
                        </TableCell>

                        {/* DATE */}

                        <TableCell
                          sx={{
                            fontFamily:
                              "GilroyMedium",

                            fontSize:
                              "14px",
                          }}
                        >
                          {
                            item.created_date
                          }
                        </TableCell>

                        {/* =========================================
                            ACTION
                        ========================================= */}

                        <TableCell
                          sx={{
                            minWidth:
                              "180px",
                          }}
                        >
                          <Box
                            sx={{
                              display:
                                "flex",

                              alignItems:
                                "center",

                              gap: 1.5,
                            }}
                          >
                            {/* ===============================
                                VIEW
                            =============================== */}

                            <Tooltip
                              title="View Quotation"
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip:
                                  {
                                    sx: {
                                      fontSize:
                                        "14px",

                                      fontFamily:
                                        "GilroyMedium",

                                      px: 1.5,
                                      py: 1,

                                      backgroundColor:
                                        "#1e293b",
                                    },
                                  },

                                arrow: {
                                  sx: {
                                    color:
                                      "#1e293b",
                                  },
                                },
                              }}
                            >
                              <IconButton
                                onClick={() =>
                                  handleAction(
                                    item,
                                  )
                                }
                                sx={{
                                  color:
                                    "#075b97",

                                  border:
                                    "1px solid #cbd5e1",

                                  borderRadius:
                                    1.5,

                                  width:
                                    42,

                                  height:
                                    42,

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        "#e8f3f9",

                                      borderColor:
                                        "#075b97",
                                    },
                                }}
                              >
                                <VisibilityOutlinedIcon
                                  sx={{
                                    fontSize:
                                      "18px",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>

                            {/* ===============================
                                DOWNLOAD
                            =============================== */}

                            <Tooltip
                              title="Download Quotation"
                              arrow
                              placement="top"
                              slotProps={{
                                tooltip:
                                  {
                                    sx: {
                                      fontSize:
                                        "14px",

                                      fontFamily:
                                        "GilroyMedium",

                                      px: 1.5,
                                      py: 1,

                                      backgroundColor:
                                        "#1e293b",
                                    },
                                  },

                                arrow: {
                                  sx: {
                                    color:
                                      "#1e293b",
                                  },
                                },
                              }}
                            >
                              <IconButton
                                onClick={() =>
                                  handleDownloadQuotation(
                                    item,
                                  )
                                }
                                sx={{
                                  color:
                                    "#075b97",

                                  border:
                                    "1px solid #cbd5e1",

                                  borderRadius:
                                    1.5,

                                  width:
                                    42,

                                  height:
                                    42,

                                  "&:hover":
                                    {
                                      backgroundColor:
                                        "#e8f3f9",

                                      borderColor:
                                        "#075b97",
                                    },
                                }}
                              >
                                <DownloadOutlinedIcon
                                  sx={{
                                    fontSize:
                                      "18px",
                                  }}
                                />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ),
                  )}

                {/* EMPTY */}

                {!priceListLoading &&
                  displayedPriceListRecords.length ===
                    0 && (
                    <TableRow>
                      <TableCell
                        colSpan={
                          7
                        }
                        sx={{
                          fontFamily:
                            "GilroyMedium",

                          fontSize:
                            "14px",

                          textAlign:
                            "center",

                          py: 3,
                        }}
                      >
                        No price list
                        records found
                      </TableCell>
                    </TableRow>
                  )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ====================================================
              PAGINATION
          ==================================================== */}

          <PaginantionComponent
            currentPage={
              priceListPagination.page
            }
            itemsPerPage={
              priceListTotalCount
            }
            totalItems={
              priceListTotalPages
            }
            disablePrevious={
              !priceListPagination.has_previous
            }
            disableNext={
              !priceListPagination.has_next
            }
            previouPageClick={() =>
              previouPageClick(
                priceListPageNumber,
                setPriceListPageNumber,
              )
            }
            nextPageClick={() =>
              nextPageClick(
                priceListPageNumber,
                setPriceListPageNumber,
                {
                  page_count:
                    priceListTotalPages,
                },
              )
            }
            pageNumber={
              priceListPageNumber
            }
            setPageNumber={
              setPriceListPageNumber
            }
          />
        </Box>
      )}

      <PriceListLineItemsModal
        open={catalogItemsOpen}
        onClose={() => setCatalogItemsOpen(false)}
        record={selectedCatalogRecord}
      />
    </Box>
  );
};

export default PriceListHome;
